import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { GraduationCap, Mail, Twitter, Linkedin, Facebook } from 'lucide-react';

const Footer = () => {
  const footerLinks = {
    product: [
      { name: 'Features', href: '/#features' },
      { name: 'DOK Levels', href: '/#dok-levels' },
      { name: 'Pricing', href: '/pricing', isRoute: true },

    ],
    support: [
      { name: 'Help Center', href: '/FAQ' },
      { name: 'Training', href: '/#training' },
      { name: 'Contact', href: '/FAQ' },
    ],
    company: [
      { name: 'About Us', href: '/#purpose' },
      { name: 'Privacy Policy', href: '/privacy-policy', internal: true },
      { name: 'Terms of Service', href: '/terms-of-service', internal: true },
    ],
    legal: [
      { name: 'Privacy Policy', href: '/privacy-policy', internal: true },
      { name: 'Terms of Service', href: '/terms-of-service', internal: true },
      { name: 'Security', href: '/#security' },
      { name: 'PIPEDA Compliance', href: '/#pipeda' }
    ]
  };

  const socialLinks = [
    { icon: Twitter, href: '#twitter', name: 'Twitter' },
    { icon: Linkedin, href: '#linkedin', name: 'LinkedIn' },
    { icon: Facebook, href: '#facebook', name: 'Facebook' }
  ];

  const renderLink = (link) => {
    if (link.internal) {
      return (
        <Link
          to={link.href}
          className="text-white/70 hover:text-white transition-colors"
        >
          {link.name}
        </Link>
      );
    }
    return (
      <a
        href={link.href}
        className="text-white/70 hover:text-white transition-colors"
      >
        {link.name}
      </a>
    );
  };

  return (
    <footer className="bg-primary text-white">
      <div className="container mx-auto px-4 py-16">
        <div className="grid lg:grid-cols-5 gap-8">
          {/* Brand Section */}
          <motion.div
            className="lg:col-span-2"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center space-x-3 mb-6">
              <GraduationCap className="w-8 h-8" />
              <span className="text-2xl font-bold">Gradely.ca</span>
            </div>
            <p className="text-white/80 text-lg leading-relaxed mb-6 max-w-md">
              Gradely empowers teachers with intelligent assessment tools and a student management platform that transforms grading into meaningful student growth.
            </p>
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <Mail className="w-4 h-4 text-white/60" />
                <span className="text-white/80">info@gradely.ca</span>
              </div>
            </div>
          </motion.div>

          {/* Links Sections */}
          <div className="w-full flex flex-col gap-4 lg:contents">
            <div className="flex flex-row w-full gap-4 lg:contents">
              {/* Product */}
              <motion.div
                className="w-1/2 lg:w-auto"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
              >
                <h3 className="text-lg font-semibold mb-4 capitalize">Product</h3>
                <ul className="space-y-3">
                  {footerLinks.product.map((link) => (
                    <li key={link.name}>{renderLink(link)}</li>
                  ))}
                </ul>
              </motion.div>

              {/* Support */}
              <motion.div
                className="w-1/2 lg:w-auto"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <h3 className="text-lg font-semibold mb-4 capitalize">Support</h3>
                <ul className="space-y-3">
                  {footerLinks.support.map((link) => (
                    <li key={link.name}>{renderLink(link)}</li>
                  ))}
                </ul>
              </motion.div>
            </div>

            {/* Company */}
            <motion.div
              className="w-full lg:w-auto"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <h3 className="text-lg font-semibold mb-4 capitalize">Company</h3>
              <ul className="space-y-3">
                {footerLinks.company.map((link) => (
                  <li key={link.name}>{renderLink(link)}</li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>

        {/* Bottom Section */}
        <motion.div
          className="border-t border-white/20 mt-12 pt-8"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          viewport={{ once: true }}
        >
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-white/70">
              <p>&copy; 2025 Gradely.ca. All rights reserved.</p>
              <p className="text-sm mt-1">Built by educators, for educators.</p>
            </div>

            {/* Social Links */}
            <div className="flex space-x-4">
              {socialLinks.map((social, index) => (
                <motion.a
                  key={social.name}
                  href={social.href}
                  className="bg-white/10 p-3 rounded-full hover:bg-white/20 transition-colors"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  initial={{ opacity: 0, scale: 0 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <social.icon className="w-5 h-5" />
                </motion.a>
              ))}
            </div>
          </div>
        </motion.div>
      </div>

      {/* Final CTA */}
      <motion.div
        className="bg-white/5 py-8"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        <div className="container mx-auto px-4 text-center">
          <p className="text-white/90 text-lg">
            Ready to transform your assessment practice?
            <a href="/#contact" className="text-accent hover:underline ml-2 font-semibold">
              Get started today →
            </a>
          </p>
        </div>
      </motion.div>
    </footer>
  );
};

export default Footer;
